// Visual R3F leaf components extracted from Game.jsx.
//
// Every component reads world state from refs each frame and mutates Three
// objects in place — there are no React `setState` calls in here. Group
// child counts are synced lazily so we never destroy & recreate meshes
// while the simulation is running.

import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Shared with the main UFO height constant in Game.jsx — used by
// `Companions` for placing the under-saucer green beam.
const UFO_HEIGHT = 10;

// Auto-laser shots — fading cyan lines from the UFO to recently-hit zombies.
export function LaserShots({ laserShotsRef }) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const g = groupRef.current;
    if (!g) return;
    const shots = laserShotsRef.current;
    while (g.children.length < shots.length) {
      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(6), 3)
      );
      const mat = new THREE.LineBasicMaterial({
        color: 0xc2ff5d,
        transparent: true,
        toneMapped: false,
      });
      g.add(new THREE.Line(geom, mat));
    }
    while (g.children.length > shots.length) {
      const c = g.children.pop();
      c.geometry.dispose();
      c.material.dispose();
    }
    const t = clock.elapsedTime;
    for (let i = 0; i < shots.length; i++) {
      const s = shots[i];
      const child = g.children[i];
      const pos = child.geometry.attributes.position.array;
      pos[0] = s.from[0]; pos[1] = s.from[1]; pos[2] = s.from[2];
      pos[3] = s.to[0]; pos[4] = s.to[1]; pos[5] = s.to[2];
      child.geometry.attributes.position.needsUpdate = true;
      const life = (t - s.born) / 0.15;
      child.material.opacity = Math.max(0, 1 - life) * 0.95;
      child.material.color.setHex(life < 0.4 ? 0xffffff : 0xc2ff5d);
    }
  });
  return <group ref={groupRef} />;
}

// Companion ships — up to 3 mini saucers floating beside the main UFO.
export function Companions({ companionsRef, count, beamActive, beamRadius }) {
  const r0 = useRef();
  const r1 = useRef();
  const r2 = useRef();
  const all = [r0, r1, r2];
  useFrame(() => {
    for (let i = 0; i < 3; i++) {
      const g = all[i].current;
      if (!g) continue;
      g.visible = i < count;
      if (i < count) {
        const c = companionsRef.current[i];
        g.position.set(c.x, c.y, c.z);
      }
    }
  });
  return (
    <>
      {[0, 1, 2].map((i) => (
        <group key={i} ref={all[i]} visible={false}>
          <mesh castShadow position={[0, 0, 0]}>
            <cylinderGeometry args={[0.85, 1.0, 0.22, 18]} />
            <meshStandardMaterial color="#cfd4dc" metalness={0.85} roughness={0.25} />
          </mesh>
          <mesh position={[0, 0.28, 0]}>
            <sphereGeometry args={[0.5, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial
              color="#7ed957"
              emissive="#3a9b1f"
              emissiveIntensity={0.6}
              transparent
              opacity={0.8}
            />
          </mesh>
          <pointLight intensity={0.4} color="#7ed957" distance={3} />
          <mesh visible={beamActive} position={[0, -UFO_HEIGHT / 2, 0]} rotation={[Math.PI, 0, 0]}>
            <coneGeometry args={[beamRadius, UFO_HEIGHT - 1, 14, 1, true]} />
            <meshStandardMaterial
              color="#7ed957"
              emissive="#7ed957"
              emissiveIntensity={2}
              transparent
              opacity={0.55}
              side={THREE.DoubleSide}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        </group>
      ))}
    </>
  );
}

// Sky-lightning bolts — short purple-white vertical streaks above struck zombies.
export function LightningStrikes({ strikesRef }) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const g = groupRef.current;
    if (!g) return;
    const strikes = strikesRef.current;
    while (g.children.length < strikes.length) {
      const geom = new THREE.BoxGeometry(0.18, 18, 0.18);
      const mat = new THREE.MeshStandardMaterial({
        color: 0xe9d4ff,
        emissive: 0xb07aff,
        emissiveIntensity: 3,
        transparent: true,
        toneMapped: false,
        depthWrite: false,
      });
      g.add(new THREE.Mesh(geom, mat));
    }
    while (g.children.length > strikes.length) {
      const c = g.children.pop();
      c.geometry.dispose();
      c.material.dispose();
    }
    const t = clock.elapsedTime;
    for (let i = 0; i < strikes.length; i++) {
      const s = strikes[i];
      const m = g.children[i];
      m.position.set(s.x, s.y + 9, s.z);
      const life = (t - s.born) / 0.18;
      const flick = Math.sin(life * 30) * 0.5 + 0.5;
      m.material.opacity = Math.max(0, 1 - life) * (0.6 + 0.4 * flick);
      m.scale.x = 0.7 + flick * 0.7;
      m.scale.z = 0.7 + flick * 0.7;
    }
  });
  return <group ref={groupRef} />;
}

// Rolling Wheels — neon torus shapes that orbit the UFO.
export function Wheels({ wheelsRef, count }) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const g = groupRef.current;
    if (!g) return;
    const arr = wheelsRef.current;
    const visible = Math.min(arr.length, count);
    while (g.children.length < 3) {
      const mesh = new THREE.Mesh(
        new THREE.TorusGeometry(0.9, 0.28, 10, 18),
        new THREE.MeshStandardMaterial({
          color: 0xfff48a,
          emissive: 0xffb347,
          emissiveIntensity: 1.2,
          metalness: 0.5,
          roughness: 0.3,
          toneMapped: false,
        })
      );
      g.add(mesh);
    }
    const t = clock.elapsedTime;
    for (let i = 0; i < 3; i++) {
      const m = g.children[i];
      if (i >= visible) {
        m.visible = false;
        continue;
      }
      const w = arr[i];
      m.visible = true;
      m.position.set(w.x, w.y + 0.2 + Math.sin(t * 4 + i) * 0.08, w.z);
      m.rotation.x = Math.PI / 2;
      m.rotation.y = t * 6 + i;
    }
  });
  return <group ref={groupRef} />;
}

// Bouncing Balls — emissive spheres ricocheting across the map.
export function Balls({ ballsRef, count }) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const g = groupRef.current;
    if (!g) return;
    const arr = ballsRef.current;
    const visible = Math.min(arr.length, count);
    while (g.children.length < 3) {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.8, 14, 12),
        new THREE.MeshStandardMaterial({
          color: 0x8af2ff,
          emissive: 0x44b4ff,
          emissiveIntensity: 1.4,
          toneMapped: false,
        })
      );
      g.add(mesh);
    }
    const t = clock.elapsedTime;
    for (let i = 0; i < 3; i++) {
      const m = g.children[i];
      if (i >= visible) {
        m.visible = false;
        continue;
      }
      const b = arr[i];
      m.visible = true;
      const hop = Math.abs(Math.sin(t * 5 + i)) * 0.9;
      m.position.set(b.x, 0.9 + hop, b.z);
      m.rotation.y = t * 3;
    }
  });
  return <group ref={groupRef} />;
}

// Super Dogs — cute round pups that chase zombies.
export function Dogs({ dogsRef, count }) {
  const groupRef = useRef();
  useFrame(({ clock }) => {
    const g = groupRef.current;
    if (!g) return;
    const arr = dogsRef.current;
    const visible = Math.min(arr.length, count);
    while (g.children.length < 3) {
      const dog = new THREE.Group();
      const body = new THREE.Mesh(
        new THREE.SphereGeometry(0.55, 14, 10),
        new THREE.MeshStandardMaterial({
          color: 0xf2c089,
          roughness: 0.7,
          metalness: 0.05,
        })
      );
      body.position.y = 0.6;
      const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.38, 12, 10),
        new THREE.MeshStandardMaterial({
          color: 0xffd9a6,
          roughness: 0.7,
        })
      );
      head.position.set(0.45, 0.85, 0);
      const ear = new THREE.Mesh(
        new THREE.ConeGeometry(0.15, 0.3, 8),
        new THREE.MeshStandardMaterial({ color: 0xc28a52, roughness: 0.7 })
      );
      ear.position.set(0.55, 1.15, 0);
      ear.rotation.z = -0.3;
      const nose = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0x2a1a0e })
      );
      nose.position.set(0.75, 0.82, 0);
      dog.add(body, head, ear, nose);
      g.add(dog);
    }
    const t = clock.elapsedTime;
    for (let i = 0; i < 3; i++) {
      const m = g.children[i];
      if (i >= visible) {
        m.visible = false;
        continue;
      }
      const d = arr[i];
      m.visible = true;
      const hop = Math.abs(Math.sin(t * 8 + i * 0.7)) * 0.18;
      m.position.set(d.x, hop, d.z);
      const facing = Math.atan2(d.vx, d.vz);
      m.rotation.y = facing;
    }
  });
  return <group ref={groupRef} />;
}

// Background starfield — generated once and rendered as plain meshes.
export function Stars() {
  const points = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 200; i++) {
      arr.push({
        x: (Math.random() - 0.5) * 200,
        y: 20 + Math.random() * 50,
        z: (Math.random() - 0.5) * 200,
        s: Math.random() * 0.15 + 0.05,
      });
    }
    return arr;
  }, []);
  return (
    <group>
      {points.map((p, i) => (
        <mesh key={i} position={[p.x, p.y, p.z]}>
          <sphereGeometry args={[p.s, 4, 4]} />
          <meshBasicMaterial color="#fff8d6" toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
